import styled from "styled-components";
import { InpatientRulesetType } from "types/InpatientRuleset";

const ExampleContainer = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 1rem;
  margin-top: 0.5rem;
`;

const FormulaLine = styled.div`
  font-family: monospace;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #495057;
`;

const ResultLine = styled.div`
  font-family: monospace;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #2e7d32;
  font-weight: 600;
`;

const ScenarioLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
`;

const fmt = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const fmtDec = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export interface IExampleFormValues {
  // Fixed Cost
  costToChargeRatio: number | null;
  fixedLossThresholdInCents: number | null;
  outlierPercent: number | null;
  // Pass Through
  thresholdInCents: number | null;
  chargeMultiplier: number | null;
  // Transfer
  losFormula: "LOS" | "LOS_PLUS_1" | "LOS_MINUS_1";
  // Stop Loss
  thresholdMultiplier: number | null;
  paymentPercent: number | null;
  // Charge Outlier
  chargeOutlierThresholdType: "FLAT" | "MULTIPLIER";
  chargeOutlierThresholdAmountInCents: number | null;
  chargeOutlierThresholdMultiplier: number | null;
  chargeOutlierPercent: number | null;
  chargeOutlierFloorInCents: number | null;
  // Inpatient Outlier (CLE-2120)
  ipoTriggerType?:
    | "STANDARD_CHARGE_THRESHOLD"
    | "MODIFIED_CHARGE_THRESHOLD"
    | "LOS_RANGE"
    | "PER_CODE_CHARGE_THRESHOLD"
    | "DISCHARGE_STATUS"
    | "ALWAYS";
  ipoCodeChargeFloorInCents?: number | null;
  ipoOnlyCodesWithoutFeeSchedule?: boolean;
  ipoDischargeStatusCodes?: string[];
  ipoLosAdjustment?: "NONE" | "PLUS_1" | "MINUS_1";
  ipoCapAtDrgRate?: boolean;
  ipoCostToChargeRatio?: number | null;
  ipoThresholdMethod?: "FIXED" | "MULTIPLIER" | "BASE_PLUS_FIXED";
  ipoBaseReference?: "BASE_PAYMENT" | "DRG_RATE_ONLY";
  ipoFixedAmountInCents?: number | null;
  ipoMultiplier?: number | null;
  ipoChargeFloorInCents?: number | null;
  ipoLosOperator?: "GT" | "GTE" | "EQ" | "LTE" | "LT";
  ipoLosDays?: number | null;
  ipoLosDaysSource?: "MANUAL" | "FEE_SCHEDULE";
  ipoFormulaType?:
    | "PER_DIEM_X_LOS"
    | "PER_DIEM_X_EXCESS_LOS"
    | "FLAT_RATE"
    | "PERCENT_OF_CHARGES"
    | "PERCENT_OF_EXCESS_CHARGES"
    | "CASE_RATE_PLUS_PER_DIEM";
  ipoCaseRateInCents?: number | null;
  ipoIncludedDays?: number | null;
  ipoCaseRatePerDiemInCents?: number | null;
  ipoPerDiemSource?:
    | "MANUAL"
    | "CASE_RATE_DIV_ALOS_TIMES_MULTIPLIER"
    | "CASE_RATE_DIV_ACTUAL_LOS"
    | "CASE_RATE_DIV_ALOS";
  ipoManualPerDiemInCents?: number | null;
  ipoExcessLosBase?: number | null;
  ipoExcessLosBaseSource?: "MANUAL" | "FEE_SCHEDULE";
  ipoFlatRateInCents?: number | null;
  ipoPercent?: number | null;
  ipoMode?: "REPLACE" | "ADDITIVE" | "MAX";
}

// Sample claim values used in examples (not configurable on the form)
const SAMPLE = {
  drgRateCents: 1500000, // $15,000
  totalChargesCents: 5000000, // $50,000
  serviceLineChargesCents: 1000000, // $10,000
  drgRateLos: 2000000, // $20,000
  alos: 5,
  losThreshold: 8,
  losMultiplier: 0.7,
  actualLos: 10,
  transferLos: 3,
  stopLossDrgCents: 1500000, // $15,000
  stopLossChargesCents: 3500000, // $35,000
  chargeOutlierBaseExpected: 1500000, // $15,000
  chargeOutlierChargesCents: 15000000, // $150,000
};

function FixedCostExample({ values }: { values: IExampleFormValues }) {
  const ccr = values.costToChargeRatio ?? 0.95;
  const thresholdCents = values.fixedLossThresholdInCents ?? 20000;
  const outlierPct = values.outlierPercent ?? 80;
  const drgRate = SAMPLE.drgRateCents;
  const charges = SAMPLE.totalChargesCents;

  const estimatedCost = Math.round(charges * ccr);
  const triggerAmount = drgRate + thresholdCents;
  const outlierApplies = estimatedCost > triggerAmount;
  const outlierPayment = outlierApplies
    ? Math.round((outlierPct / 100) * (estimatedCost - triggerAmount))
    : 0;
  const expected = drgRate + outlierPayment;

  return (
    <ExampleContainer>
      <ScenarioLabel>Example Calculation</ScenarioLabel>
      <FormulaLine>DRG Rate (base expected) = {fmt(drgRate)}</FormulaLine>
      <FormulaLine>Total Charges = {fmt(charges)}</FormulaLine>
      <FormulaLine>
        CCR = {ccr}, Threshold = {fmtDec(thresholdCents)}, Outlier% ={" "}
        {outlierPct}%
      </FormulaLine>

      <ScenarioLabel style={{ marginTop: "0.75rem" }}>Steps</ScenarioLabel>
      <FormulaLine>
        1. Estimated Cost = {fmt(charges)} × {ccr} = {fmt(estimatedCost)}
      </FormulaLine>
      <FormulaLine>
        2. Trigger Amount = {fmt(drgRate)} + {fmtDec(thresholdCents)} ={" "}
        {fmt(triggerAmount)}
      </FormulaLine>
      {outlierApplies ? (
        <>
          <FormulaLine>
            3. {fmt(estimatedCost)} &gt; {fmt(triggerAmount)} → outlier applies
          </FormulaLine>
          <FormulaLine>
            4. Outlier Payment = {outlierPct}% × ({fmt(estimatedCost)} −{" "}
            {fmt(triggerAmount)}) = {fmt(outlierPayment)}
          </FormulaLine>
          <ResultLine>
            Expected = {fmt(drgRate)} + {fmt(outlierPayment)} = {fmt(expected)}
          </ResultLine>
        </>
      ) : (
        <>
          <FormulaLine>
            3. {fmt(estimatedCost)} ≤ {fmt(triggerAmount)} → no outlier
          </FormulaLine>
          <ResultLine>Expected = {fmt(drgRate)} (base DRG rate)</ResultLine>
        </>
      )}

      <div
        className="text-600 mt-2"
        style={{ fontSize: "0.8rem", fontStyle: "italic" }}
      >
        If Estimated Cost is below the Trigger Amount, no outlier applies and
        the expected amount remains at the base DRG rate.
      </div>
    </ExampleContainer>
  );
}

function PassThroughExample({ values }: { values: IExampleFormValues }) {
  const thresholdCents = values.thresholdInCents ?? 0;
  const multiplier = values.chargeMultiplier ?? 1.25;
  const charges = SAMPLE.serviceLineChargesCents;

  const applies = charges > thresholdCents;
  const expected = applies ? Math.round(charges * multiplier) : 0;

  return (
    <ExampleContainer>
      <ScenarioLabel>Example Calculation</ScenarioLabel>
      <FormulaLine>Service line charges = {fmt(charges)}</FormulaLine>
      <FormulaLine>
        Charge Threshold = {fmtDec(thresholdCents)}, Charge Multiplier ={" "}
        {multiplier}
      </FormulaLine>

      <ScenarioLabel style={{ marginTop: "0.75rem" }}>Steps</ScenarioLabel>
      {applies ? (
        <>
          <FormulaLine>
            1. {fmt(charges)} &gt; {fmtDec(thresholdCents)} threshold →
            pass-through applies
          </FormulaLine>
          <FormulaLine>
            2. Expected = {fmt(charges)} × {multiplier} = {fmt(expected)}
          </FormulaLine>
          <ResultLine>
            Pass-through amount added to base expected = {fmt(expected)}
          </ResultLine>
        </>
      ) : (
        <>
          <FormulaLine>
            1. {fmt(charges)} ≤ {fmtDec(thresholdCents)} threshold →
            pass-through does not apply
          </FormulaLine>
          <ResultLine>No pass-through amount added</ResultLine>
        </>
      )}

      <div
        className="text-600 mt-2"
        style={{ fontSize: "0.8rem", fontStyle: "italic" }}
      >
        Pass-through pricing is used for service line codes that have no fee
        schedule match. Each matching code&apos;s charges are multiplied
        individually, then summed.
      </div>
    </ExampleContainer>
  );
}

function ExcessiveLosExample() {
  const drgRate = SAMPLE.drgRateLos;
  const { alos } = SAMPLE;
  const threshold = SAMPLE.losThreshold;
  const multiplier = SAMPLE.losMultiplier;
  const los = SAMPLE.actualLos;

  const perDiem = Math.round(multiplier * (drgRate / alos));
  const outlierDays = los - threshold;
  const addOn = perDiem * outlierDays;
  const expected = drgRate + addOn;

  return (
    <ExampleContainer>
      <ScenarioLabel>Example Calculation</ScenarioLabel>
      <FormulaLine>DRG Base Rate = {fmt(drgRate)}</FormulaLine>
      <FormulaLine>ALOS/GLOS = {alos} days</FormulaLine>
      <FormulaLine>
        Outlier Threshold = {threshold} days, Multiplier = {multiplier}
      </FormulaLine>
      <FormulaLine>Actual LOS = {los} days</FormulaLine>

      <ScenarioLabel style={{ marginTop: "0.75rem" }}>Steps</ScenarioLabel>
      <FormulaLine>
        1. {los} days &gt; {threshold} day threshold → day outlier applies
      </FormulaLine>
      <FormulaLine>
        2. Per Diem = {multiplier} × ({fmt(drgRate)} ÷ {alos}) = {fmt(perDiem)}
        /day
      </FormulaLine>
      <FormulaLine>
        3. Outlier Days = {los} − {threshold} = {outlierDays} days
      </FormulaLine>
      <FormulaLine>
        4. Outlier Add-on = {fmt(perDiem)} × {outlierDays} = {fmt(addOn)}
      </FormulaLine>
      <ResultLine>
        Expected = {fmt(drgRate)} + {fmt(addOn)} = {fmt(expected)}
      </ResultLine>

      <div
        className="text-600 mt-2"
        style={{ fontSize: "0.8rem", fontStyle: "italic" }}
      >
        The ALOS/GLOS, outlier threshold, and multiplier come from the DRG
        fields on the ProviderProcedure record. If LOS does not exceed the
        threshold, the base DRG rate is used with no add-on.
      </div>
    </ExampleContainer>
  );
}

function TransferExample({ values }: { values: IExampleFormValues }) {
  const { losFormula } = values;
  const drgRate = SAMPLE.drgRateLos;
  const { alos } = SAMPLE;
  const los = SAMPLE.transferLos;

  const perDiem = Math.round(drgRate / alos);
  const losFormulaMap = {
    LOS_PLUS_1: {
      days: los + 1,
      label: "Per Diem × (LOS + 1)",
      calc: `${los} + 1 = ${los + 1}`,
    },
    LOS_MINUS_1: {
      days: Math.max(1, los - 1),
      label: "Per Diem × (LOS - 1)",
      calc: `${los} - 1 = ${Math.max(1, los - 1)}`,
    },
    LOS: {
      days: los,
      label: "Per Diem × LOS",
      calc: `${los}`,
    },
  };
  const {
    days: losDays,
    label: formulaLabel,
    calc: losDaysCalc,
  } = losFormulaMap[losFormula];
  const transferPayment = perDiem * losDays;
  const expected = Math.min(drgRate, transferPayment);

  return (
    <ExampleContainer>
      <ScenarioLabel>Example Calculation</ScenarioLabel>
      <FormulaLine>DRG Base Rate = {fmt(drgRate)}</FormulaLine>
      <FormulaLine>ALOS/GLOS = {alos} days</FormulaLine>
      <FormulaLine>Actual LOS = {los} days</FormulaLine>
      <FormulaLine>LOS Formula = {formulaLabel}</FormulaLine>
      <FormulaLine>Discharge Status = 02 (transferred)</FormulaLine>

      <ScenarioLabel style={{ marginTop: "0.75rem" }}>Steps</ScenarioLabel>
      <FormulaLine>
        1. Discharge status 02 matches configured codes → transfer applies
      </FormulaLine>
      <FormulaLine>
        2. Per Diem = {fmt(drgRate)} ÷ {alos} = {fmt(perDiem)}/day
      </FormulaLine>
      <FormulaLine>
        3. LOS Days = {losDaysCalc} (using {formulaLabel} formula)
      </FormulaLine>
      <FormulaLine>
        4. Transfer Payment = {fmt(perDiem)} × {losDays} ={" "}
        {fmt(transferPayment)}
      </FormulaLine>
      <FormulaLine>
        5. Cap at DRG Rate: min({fmt(drgRate)}, {fmt(transferPayment)}) ={" "}
        {fmt(expected)}
      </FormulaLine>
      <ResultLine>Expected = {fmt(expected)}</ResultLine>

      <div
        className="text-600 mt-2"
        style={{ fontSize: "0.8rem", fontStyle: "italic" }}
      >
        Transfer pricing is capped at the full DRG rate — if Per Diem × days
        exceeds the DRG rate, the DRG rate is used instead. Only claims with a
        matching discharge status code trigger transfer pricing.
      </div>
    </ExampleContainer>
  );
}

function StopLossExample({ values }: { values: IExampleFormValues }) {
  const thresholdMult = values.thresholdMultiplier ?? 200;
  const paymentPct = values.paymentPercent ?? 75;
  const drgRate = SAMPLE.stopLossDrgCents;
  const charges = SAMPLE.stopLossChargesCents;

  const thresholdAmount = Math.round(drgRate * (thresholdMult / 100));
  const applies = charges > thresholdAmount;
  const stopLossPayment = applies
    ? Math.round((paymentPct / 100) * charges)
    : 0;

  return (
    <ExampleContainer>
      <ScenarioLabel>Example Calculation</ScenarioLabel>
      <FormulaLine>DRG Rate (base expected) = {fmt(drgRate)}</FormulaLine>
      <FormulaLine>Total Billed Charges = {fmt(charges)}</FormulaLine>
      <FormulaLine>
        Threshold = {thresholdMult}% of DRG, Payment = {paymentPct}% of charges
      </FormulaLine>

      <ScenarioLabel style={{ marginTop: "0.75rem" }}>Steps</ScenarioLabel>
      <FormulaLine>
        1. Threshold Amount = {fmt(drgRate)} × {thresholdMult}% ={" "}
        {fmt(thresholdAmount)}
      </FormulaLine>
      {applies ? (
        <>
          <FormulaLine>
            2. {fmt(charges)} &gt; {fmt(thresholdAmount)} → stop loss applies
          </FormulaLine>
          <FormulaLine>
            3. Stop Loss Payment = {paymentPct}% × {fmt(charges)} ={" "}
            {fmt(stopLossPayment)}
          </FormulaLine>
          <ResultLine>
            Expected = {fmt(stopLossPayment)} (replaces DRG rate)
          </ResultLine>
        </>
      ) : (
        <>
          <FormulaLine>
            2. {fmt(charges)} ≤ {fmt(thresholdAmount)} → stop loss does not
            apply
          </FormulaLine>
          <ResultLine>Expected = {fmt(drgRate)} (base DRG rate)</ResultLine>
        </>
      )}

      <div
        className="text-600 mt-2"
        style={{ fontSize: "0.8rem", fontStyle: "italic" }}
      >
        Stop loss replaces the base expected amount entirely (it is not added to
        the DRG rate). If charges do not exceed the threshold, stop loss does
        not apply and the DRG rate is used.
      </div>
    </ExampleContainer>
  );
}

function ChargeOutlierExample({ values }: { values: IExampleFormValues }) {
  const thresholdType = values.chargeOutlierThresholdType ?? "FLAT";
  const outlierPct = values.chargeOutlierPercent ?? 30;
  const baseExpected = SAMPLE.chargeOutlierBaseExpected;
  const charges = SAMPLE.chargeOutlierChargesCents;
  const floorCents = values.chargeOutlierFloorInCents;

  let threshold: number;
  let thresholdLabel: string;
  if (thresholdType === "FLAT") {
    const flatAmount = values.chargeOutlierThresholdAmountInCents ?? 12500000;
    threshold = flatAmount;
    thresholdLabel = `Flat threshold = ${fmtDec(flatAmount)}`;
  } else {
    const multiplier = values.chargeOutlierThresholdMultiplier ?? 3.0;
    threshold = Math.round(multiplier * baseExpected);
    thresholdLabel = `Threshold = ${multiplier} \u00d7 ${fmt(
      baseExpected
    )} = ${fmt(threshold)}`;
  }

  const meetsFloor = !floorCents || charges > floorCents;
  const applies = meetsFloor && charges > threshold;
  const excessCharges = applies ? charges - threshold : 0;
  const outlierPayment = applies
    ? Math.round((outlierPct / 100) * excessCharges)
    : 0;
  const expected = baseExpected + outlierPayment;

  return (
    <ExampleContainer>
      <ScenarioLabel>Example Calculation</ScenarioLabel>
      <FormulaLine>
        Base Expected (DRG/Per Diem) = {fmt(baseExpected)}
      </FormulaLine>
      <FormulaLine>Total Billed Charges = {fmt(charges)}</FormulaLine>
      <FormulaLine>
        {thresholdLabel}, Outlier% = {outlierPct}%
        {floorCents ? `, Charge Floor = ${fmtDec(floorCents)}` : ""}
      </FormulaLine>

      <ScenarioLabel style={{ marginTop: "0.75rem" }}>Steps</ScenarioLabel>
      {floorCents && (
        <FormulaLine>
          1. Charge floor check: {fmt(charges)} {meetsFloor ? ">" : "\u2264"}{" "}
          {fmtDec(floorCents)}{" "}
          {meetsFloor ? "\u2192 passes" : "\u2192 fails, no outlier"}
        </FormulaLine>
      )}
      <FormulaLine>
        {floorCents ? "2" : "1"}. Threshold: {thresholdLabel}
      </FormulaLine>
      {applies ? (
        <>
          <FormulaLine>
            {floorCents ? "3" : "2"}. {fmt(charges)} &gt; {fmt(threshold)}{" "}
            &rarr; outlier applies
          </FormulaLine>
          <FormulaLine>
            {floorCents ? "4" : "3"}. Excess = {fmt(charges)} &minus;{" "}
            {fmt(threshold)} = {fmt(excessCharges)}
          </FormulaLine>
          <FormulaLine>
            {floorCents ? "5" : "4"}. Outlier Payment = {outlierPct}% &times;{" "}
            {fmt(excessCharges)} = {fmt(outlierPayment)}
          </FormulaLine>
          <ResultLine>
            Expected = {fmt(baseExpected)} + {fmt(outlierPayment)} ={" "}
            {fmt(expected)}
          </ResultLine>
        </>
      ) : (
        <>
          <FormulaLine>
            {floorCents ? "3" : "2"}. {fmt(charges)} &le; {fmt(threshold)}{" "}
            &rarr; no outlier
          </FormulaLine>
          <ResultLine>Expected = {fmt(baseExpected)} (base only)</ResultLine>
        </>
      )}

      <div
        className="text-600 mt-2"
        style={{ fontSize: "0.8rem", fontStyle: "italic" }}
      >
        The outlier payment is added to the base expected amount. The threshold
        is computed from{" "}
        {thresholdType === "FLAT"
          ? "a fixed dollar amount"
          : "a multiplier of the base expected (DRG rate or per diem \u00d7 LOS)"}
        .
        {floorCents
          ? " Both the charge floor and threshold must be exceeded for the outlier to apply."
          : ""}
      </div>
    </ExampleContainer>
  );
}

function InpatientOutlierExample({ values }: { values: IExampleFormValues }) {
  const triggerType = values.ipoTriggerType ?? "STANDARD_CHARGE_THRESHOLD";
  const thresholdMethod = values.ipoThresholdMethod ?? "FIXED";
  const baseReference = values.ipoBaseReference ?? "BASE_PAYMENT";
  const formulaType = values.ipoFormulaType ?? "PERCENT_OF_EXCESS_CHARGES";
  const mode = values.ipoMode ?? "ADDITIVE";

  // Sample claim numbers
  const charges = SAMPLE.chargeOutlierChargesCents; // $150,000
  const basePayment = SAMPLE.chargeOutlierBaseExpected; // $15,000
  const drgOnly = SAMPLE.drgRateCents; // $15,000
  // Sample LOS = 10 so LOS Range triggers (default GT 8 days for fee-schedule
  // sources) demonstrate firing instead of "does not fire". Matches Ani's worked
  // example for Rule 8 BCBST day outlier (CLE-2120 v3 spec, Linear comment 1.2).
  const sampleLos = 10;
  const sampleAlos = SAMPLE.alos;
  const sampleDrgPrice = SAMPLE.drgRateLos;
  const sampleLosThreshold = SAMPLE.losThreshold;
  const sampleOutlierMult = SAMPLE.losMultiplier;

  // ── Trigger evaluation ─────────────────────────────────
  let fired = false;
  let resolvedThreshold: number | null = null;
  let effectiveCharges: number | null = null;
  let triggerLine = "";

  if (
    triggerType === "STANDARD_CHARGE_THRESHOLD" ||
    triggerType === "MODIFIED_CHARGE_THRESHOLD"
  ) {
    const ccr =
      triggerType === "MODIFIED_CHARGE_THRESHOLD"
        ? values.ipoCostToChargeRatio ?? 0.95
        : 1;
    effectiveCharges = Math.round(charges * ccr);
    const baseValue = baseReference === "BASE_PAYMENT" ? basePayment : drgOnly;
    if (thresholdMethod === "FIXED") {
      resolvedThreshold = values.ipoFixedAmountInCents ?? 12500000;
      triggerLine = `Threshold = ${fmt(resolvedThreshold)} (fixed)`;
    } else if (thresholdMethod === "MULTIPLIER") {
      const mult = values.ipoMultiplier ?? 3;
      resolvedThreshold = Math.round(baseValue * mult);
      triggerLine = `Threshold = ${mult} × ${fmt(baseValue)} = ${fmt(
        resolvedThreshold
      )}`;
    } else {
      const fixed = values.ipoFixedAmountInCents ?? 4600000;
      resolvedThreshold = baseValue + fixed;
      triggerLine = `Threshold = ${fmt(baseValue)} + ${fmt(fixed)} = ${fmt(
        resolvedThreshold
      )}`;
    }
    const floor = values.ipoChargeFloorInCents;
    const meetsFloor = !floor || effectiveCharges > floor;
    fired = meetsFloor && effectiveCharges > resolvedThreshold;
  } else if (triggerType === "LOS_RANGE") {
    const days =
      values.ipoLosDaysSource === "FEE_SCHEDULE"
        ? sampleLosThreshold
        : values.ipoLosDays ?? 2;
    const op = values.ipoLosOperator ?? "GT";
    const ops: Record<string, (a: number, b: number) => boolean> = {
      GT: (a, b) => a > b,
      GTE: (a, b) => a >= b,
      EQ: (a, b) => a === b,
      LTE: (a, b) => a <= b,
      LT: (a, b) => a < b,
    };
    fired = ops[op](sampleLos, days);
    triggerLine = `LOS ${sampleLos} ${op} ${days} days → ${
      fired ? "fires" : "does not fire"
    }`;
  } else if (triggerType === "PER_CODE_CHARGE_THRESHOLD") {
    // Sample: one matching service line at $20,000
    const sampleLineCharges = 2000000;
    const floor = values.ipoCodeChargeFloorInCents ?? 0;
    fired = sampleLineCharges > floor;
    effectiveCharges = sampleLineCharges;
    resolvedThreshold = floor;
    triggerLine = `Sample line charges ${fmt(sampleLineCharges)} ${
      fired ? ">" : "≤"
    } floor ${fmt(floor)} → ${
      fired ? "fires for this line" : "below floor"
    } (rule iterates per matching line and sums)`;
  } else if (triggerType === "DISCHARGE_STATUS") {
    const sampleStatus = "02";
    fired = (values.ipoDischargeStatusCodes ?? []).includes(sampleStatus);
    triggerLine = `Sample discharge status ${sampleStatus} ${
      fired ? "is" : "is not"
    } in configured codes [${(values.ipoDischargeStatusCodes ?? []).join(
      ", "
    )}] → ${fired ? "fires" : "does not fire"}`;
  } else if (triggerType === "ALWAYS") {
    fired = true;
    triggerLine = "Always fires (no conditions — DRG match is the only gate)";
  }

  // ── Formula evaluation (only if fired) ───────────────────
  let calculatedAmount = 0;
  let formulaLine = "";
  if (fired) {
    if (formulaType === "PER_DIEM_X_LOS") {
      let perDiem: number;
      if (values.ipoPerDiemSource === "MANUAL") {
        perDiem = values.ipoManualPerDiemInCents ?? 482700;
      } else if (values.ipoPerDiemSource === "CASE_RATE_DIV_ACTUAL_LOS") {
        perDiem = Math.round(sampleDrgPrice / sampleLos);
      } else if (values.ipoPerDiemSource === "CASE_RATE_DIV_ALOS") {
        perDiem = Math.round(sampleDrgPrice / sampleAlos);
      } else {
        perDiem = Math.round((sampleDrgPrice / sampleAlos) * sampleOutlierMult);
      }
      const adjust = values.ipoLosAdjustment ?? "NONE";
      let effectiveLos = sampleLos;
      if (adjust === "PLUS_1") effectiveLos = sampleLos + 1;
      else if (adjust === "MINUS_1") effectiveLos = Math.max(1, sampleLos - 1);
      let amount = perDiem * effectiveLos;
      let adjustLabel: string;
      if (adjust === "PLUS_1") adjustLabel = `(LOS + 1) = ${effectiveLos}`;
      else if (adjust === "MINUS_1")
        adjustLabel = `max(1, LOS - 1) = ${effectiveLos}`;
      else adjustLabel = `LOS ${effectiveLos}`;
      let cappedNote = "";
      if (values.ipoCapAtDrgRate && amount > sampleDrgPrice) {
        cappedNote = `, capped at DRG rate ${fmt(sampleDrgPrice)}`;
        amount = sampleDrgPrice;
      }
      calculatedAmount = amount;
      formulaLine = `Per Diem ${fmt(perDiem)} × ${adjustLabel} = ${fmt(
        calculatedAmount
      )}${cappedNote}`;
    } else if (formulaType === "PER_DIEM_X_EXCESS_LOS") {
      let perDiem: number;
      if (values.ipoPerDiemSource === "MANUAL") {
        perDiem = values.ipoManualPerDiemInCents ?? 159100;
      } else if (values.ipoPerDiemSource === "CASE_RATE_DIV_ACTUAL_LOS") {
        perDiem = Math.round(sampleDrgPrice / sampleLos);
      } else if (values.ipoPerDiemSource === "CASE_RATE_DIV_ALOS") {
        perDiem = Math.round(sampleDrgPrice / sampleAlos);
      } else {
        perDiem = Math.round((sampleDrgPrice / sampleAlos) * sampleOutlierMult);
      }
      const excessBase =
        values.ipoExcessLosBaseSource === "FEE_SCHEDULE"
          ? sampleLosThreshold
          : values.ipoExcessLosBase ?? 2;
      const excess = Math.max(0, sampleLos - excessBase);
      calculatedAmount = perDiem * excess;
      formulaLine = `Per Diem ${fmt(
        perDiem
      )} × Excess LOS (${sampleLos} − ${excessBase} = ${excess}) = ${fmt(
        calculatedAmount
      )}`;
    } else if (formulaType === "FLAT_RATE") {
      calculatedAmount = values.ipoFlatRateInCents ?? 435000;
      formulaLine = `Flat rate = ${fmt(calculatedAmount)}`;
    } else if (formulaType === "PERCENT_OF_CHARGES") {
      const pct = values.ipoPercent ?? 70;
      // For per-code trigger, the formula uses the line's charges (effectiveCharges set above)
      const baseCharges = effectiveCharges ?? charges;
      calculatedAmount = Math.round((pct / 100) * baseCharges);
      formulaLine = `${pct}% × ${fmt(baseCharges)} = ${fmt(calculatedAmount)}`;
    } else if (formulaType === "PERCENT_OF_EXCESS_CHARGES") {
      const pct = values.ipoPercent ?? 30;
      const excess = Math.max(
        0,
        (effectiveCharges ?? 0) - (resolvedThreshold ?? 0)
      );
      calculatedAmount = Math.round((pct / 100) * excess);
      formulaLine = `${pct}% × Excess (${fmt(effectiveCharges ?? 0)} − ${fmt(
        resolvedThreshold ?? 0
      )} = ${fmt(excess)}) = ${fmt(calculatedAmount)}`;
    } else if (formulaType === "CASE_RATE_PLUS_PER_DIEM") {
      const caseRate = values.ipoCaseRateInCents ?? 579600;
      const includedDays = values.ipoIncludedDays ?? 2;
      const perDiem = values.ipoCaseRatePerDiemInCents ?? 159100;
      const excessDays = Math.max(0, sampleLos - includedDays);
      calculatedAmount = caseRate + perDiem * excessDays;
      formulaLine = `Case rate ${fmt(caseRate)} + Per diem ${fmt(
        perDiem
      )} × max(0, LOS ${sampleLos} − ${includedDays}) = ${fmt(
        calculatedAmount
      )}`;
    }
  }

  // ── Mode application ───────────────────────────────────
  let final: number;
  let modeLine: string;
  if (mode === "REPLACE") {
    final = calculatedAmount;
    modeLine = `Replace → ${fmt(calculatedAmount)}`;
  } else if (mode === "ADDITIVE") {
    final = basePayment + calculatedAmount;
    modeLine = `Add → ${fmt(basePayment)} + ${fmt(calculatedAmount)} = ${fmt(
      final
    )}`;
  } else {
    final = Math.max(basePayment, calculatedAmount);
    modeLine = `Max → max(${fmt(basePayment)}, ${fmt(
      calculatedAmount
    )}) = ${fmt(final)}`;
  }

  return (
    <ExampleContainer>
      <ScenarioLabel>Example Calculation</ScenarioLabel>
      <FormulaLine>Total Charges = {fmt(charges)}</FormulaLine>
      <FormulaLine>
        Base Payment (837 + LOS + pass-through) = {fmt(basePayment)}
      </FormulaLine>
      <FormulaLine>DRG Rate Only (raw 837) = {fmt(drgOnly)}</FormulaLine>
      <FormulaLine>
        Case Rate (fee schedule) = {fmt(sampleDrgPrice)}, ALOS = {sampleAlos}{" "}
        days
      </FormulaLine>
      <FormulaLine>Sample LOS = {sampleLos} days</FormulaLine>

      <ScenarioLabel style={{ marginTop: "0.75rem" }}>Trigger</ScenarioLabel>
      <FormulaLine>{triggerLine}</FormulaLine>
      {effectiveCharges !== null && resolvedThreshold !== null && (
        <FormulaLine>
          Effective Charges {fmt(effectiveCharges)} {fired ? ">" : "≤"}{" "}
          {fmt(resolvedThreshold)} → {fired ? "fires" : "does not fire"}
        </FormulaLine>
      )}

      {fired ? (
        <>
          <ScenarioLabel style={{ marginTop: "0.75rem" }}>
            Formula
          </ScenarioLabel>
          <FormulaLine>{formulaLine}</FormulaLine>
          <ScenarioLabel style={{ marginTop: "0.75rem" }}>Mode</ScenarioLabel>
          <FormulaLine>{modeLine}</FormulaLine>
          <ResultLine>Expected = {fmt(final)}</ResultLine>
        </>
      ) : (
        <ResultLine>
          Trigger does not fire → no payment from this rule
        </ResultLine>
      )}

      <div
        className="text-600 mt-2"
        style={{ fontSize: "0.8rem", fontStyle: "italic" }}
      >
        Inpatient Outlier Rules combine a trigger, a formula, and a mode.
        Multiple rules can stack on the same DRG — Add rules sum, Max rules
        compete with Math.max(), and the first Replace rule wins (overrides
        everything including Transfer pricing).
      </div>
    </ExampleContainer>
  );
}

export interface IInpatientCalculationExampleProps {
  type: InpatientRulesetType;
  values: IExampleFormValues;
}

export function InpatientCalculationExample({
  type,
  values,
}: IInpatientCalculationExampleProps) {
  switch (type) {
    case "FIXED_COST":
      return (
        <div className="mt-3">
          <FixedCostExample values={values} />
        </div>
      );
    case "PASS_THROUGH":
      return (
        <div className="mt-3">
          <PassThroughExample values={values} />
        </div>
      );
    case "EXCESSIVE_LOS":
      return (
        <div className="mt-3">
          <ExcessiveLosExample />
        </div>
      );
    case "TRANSFER":
      return (
        <div className="mt-3">
          <TransferExample values={values} />
        </div>
      );
    case "STOP_LOSS":
      return (
        <div className="mt-3">
          <StopLossExample values={values} />
        </div>
      );
    case "CHARGE_OUTLIER":
      return (
        <div className="mt-3">
          <ChargeOutlierExample values={values} />
        </div>
      );
    case "INPATIENT_OUTLIER":
      return (
        <div className="mt-3">
          <InpatientOutlierExample values={values} />
        </div>
      );
    default:
      return null;
  }
}
